import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, getDoc, updateDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { ApplicationFormData, Application, ApiResponse } from '../types/application';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Generate next sequential application ID from database
const generateNextApplicationId = async (): Promise<string> => {
  try {
    // Get all applications and find the highest numeric ID
    const q = query(collection(db, 'applications'));
    const querySnapshot = await getDocs(q);
    
    let nextId = 310101; // Starting ID if no applications exist
    
    if (!querySnapshot.empty) {
      let maxId = 310100; // Start below our range
      
      querySnapshot.docs.forEach(doc => {
        const docId = parseInt(doc.data().id);
        if (!isNaN(docId) && docId >= 310101) {
          maxId = Math.max(maxId, docId);
        }
      });
      
      // If we found any valid IDs in our range, increment the max
      if (maxId >= 310101) {
        nextId = maxId + 1;
      }
    }
    
    console.log('🔢 Generated next application ID:', nextId);
    return nextId.toString();
  } catch (error) {
    console.error('❌ Error generating application ID, using timestamp fallback:', error);
    // Fallback: use timestamp-based ID if database query fails
    const timestamp = Date.now();
    const fallbackId = parseInt('310' + timestamp.toString().slice(-3));
    return fallbackId.toString();
  }
};

// Get brand contact email
const getBrandContactEmail = (brandRef?: string): string => {
  const brandId = brandRef || 'finaconnect';
  return `applications+${brandId}@finaconnect.com`;
};

export const firebaseAPI = {
  // Submit new application
  submitApplication: async (data: ApplicationFormData): Promise<ApiResponse<{ applicationId: string; status: string; submittedAt: string }>> => {
    try {
      console.log('🔥 Starting Firebase submission...');
      console.log('🔥 Environment check:', {
        NODE_ENV: process.env.NODE_ENV,
        hasAPIKey: !!process.env.REACT_APP_FIREBASE_API_KEY,
        hasProjectId: !!process.env.REACT_APP_FIREBASE_PROJECT_ID,
        usingFallback: !process.env.REACT_APP_FIREBASE_API_KEY,
        projectId: firebaseConfig.projectId
      });
      
      let applicationId = await generateNextApplicationId();
      const submittedAt = new Date().toISOString();
      
      let applicationData = {
        id: applicationId,
        data,
        status: 'submitted',
        submittedAt,
        updatedAt: submittedAt,
        submittedBy: data.businessEmail,
        documents: []
      };

      console.log('🔥 Application data prepared:', { id: applicationId, businessEmail: data.businessEmail });
      console.log('🔥 Firebase config check:', { 
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY ? 'Set' : 'Missing'
      });

      // Add timeout to Firebase operation
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Firebase operation timed out after 30 seconds')), 30000);
      });

      // Add document with retry logic for potential ID conflicts
      let attempts = 0;
      let docRef;
      const maxAttempts = 3;
      
      while (attempts < maxAttempts) {
        try {
          docRef = await Promise.race([
            addDoc(collection(db, 'applications'), applicationData),
            timeoutPromise
          ]) as any;
          console.log('🔥 Document written with ID:', docRef.id, 'Application ID:', applicationId);
          break; // Success, exit retry loop
        } catch (error: any) {
          attempts++;
          if (attempts >= maxAttempts) {
            throw error; // Give up after max attempts
          }
          
          // If it's an ID conflict, generate new ID and retry
          console.log(`⚠️ Attempt ${attempts} failed, retrying with new ID...`);
          applicationId = await generateNextApplicationId();
          applicationData = {
            ...applicationData,
            id: applicationId
          };
        }
      }

      // Send email notifications via Netlify Function
      try {
        console.log('📧 Attempting to send email notifications...');
        const emailResponse = await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            applicationData,
            brandInfo: {
              name: (data as any).brandName || 'FinaConnect',
              contactEmail: getBrandContactEmail((data as any).brandRef),
            }
          }),
        });
        
        if (emailResponse.ok) {
          console.log('✅ Email notifications sent for application:', applicationId);
        } else {
          console.warn('⚠️ Email sending failed (status ' + emailResponse.status + '), but application was saved:', applicationId);
          const errorText = await emailResponse.text().catch(() => 'Unknown error');
          console.warn('Email error details:', errorText);
        }
      } catch (emailError) {
        console.warn('⚠️ Email function unavailable, but application was saved:', applicationId, emailError);
      }
      
      return {
        success: true,
        data: {
          applicationId,
          status: 'submitted',
          submittedAt
        },
        message: 'Application submitted successfully'
      };
    } catch (error: any) {
      console.error('🔥 Firebase submission error:', error);
      console.error('🔥 Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      let userFriendlyError = 'Failed to submit application';
      if (error.code === 'permission-denied') {
        userFriendlyError = 'Database permission denied. Please check Firestore security rules.';
      } else if (error.code === 'unavailable') {
        userFriendlyError = 'Database temporarily unavailable. Please try again.';
      } else if (error.message) {
        userFriendlyError = error.message;
      }
      
      return {
        success: false,
        error: userFriendlyError
      };
    }
  },

  // Get application by ID
  getApplication: async (id: string): Promise<ApiResponse<Application>> => {
    try {
      const q = query(collection(db, 'applications'), where('id', '==', id));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return {
          success: false,
          error: 'Application not found'
        };
      }

      const doc = querySnapshot.docs[0];
      const application = doc.data() as Application;

      return {
        success: true,
        data: application
      };
    } catch (error: any) {
      console.error('Firebase get application error:', error);
      return {
        success: false,
        error: error.message || 'Failed to retrieve application'
      };
    }
  },

  // Get all applications (admin)
  getApplications: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    applications: Application[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>> => {
    try {
      let q = query(collection(db, 'applications'), orderBy('submittedAt', 'desc'));
      
      if (params?.status) {
        q = query(collection(db, 'applications'), 
          where('status', '==', params.status), 
          orderBy('submittedAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      const applications = querySnapshot.docs.map(doc => doc.data() as Application);

      // Simple pagination (Firebase has better ways but this works for demo)
      const page = params?.page || 1;
      const limit = params?.limit || 10;
      const startIndex = (page - 1) * limit;
      const paginatedApps = applications.slice(startIndex, startIndex + limit);

      return {
        success: true,
        data: {
          applications: paginatedApps,
          pagination: {
            page,
            limit,
            total: applications.length,
            totalPages: Math.ceil(applications.length / limit)
          }
        }
      };
    } catch (error: any) {
      console.error('Firebase get applications error:', error);
      return {
        success: false,
        error: error.message || 'Failed to retrieve applications'
      };
    }
  },

  // Update application status (admin)
  updateApplicationStatus: async (
    id: string,
    status: string,
    reviewNotes?: string
  ): Promise<ApiResponse<Application>> => {
    try {
      const q = query(collection(db, 'applications'), where('id', '==', id));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return {
          success: false,
          error: 'Application not found'
        };
      }

      const docRef = querySnapshot.docs[0].ref;
      await updateDoc(docRef, {
        status,
        reviewNotes,
        updatedAt: new Date().toISOString()
      });

      // Get updated application
      const updatedDoc = await getDoc(docRef);
      const application = updatedDoc.data() as Application;

      return {
        success: true,
        data: application,
        message: 'Application status updated successfully'
      };
    } catch (error: any) {
      console.error('Firebase update status error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update application status'
      };
    }
  }
};